import { useEffect, useRef } from 'react'

import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import { useAppDispatch } from 'src/Hooks/redux'
import {
  Order,
  OrderItem,
  OrderItemType,
  useAddOrderItemMutation,
  useCreateOrderMutation,
  useRemoveOrderItemsMutation,
  useUpdateOrderItemMutation,
} from 'src/Redux/artistOrderApi'
import { isOrderLocked } from 'src/Redux/artistOrderApi/isOrderLocked'
import { setPaymentAlertError } from 'src/Redux/payment-process'

interface UseTikTokOrderSyncParams {
  campaignId: number
  budget: number
  isPaymentSucceeded: boolean
  currentOrder?: Order | null
  isOrderFetching: boolean
  onOrderSynced: () => Promise<void>
}

const isSameCampaignOrderItem = (
  item: OrderItem,
  campaignId: number,
): boolean =>
  item.sourceId === campaignId &&
  (item.type === OrderItemType.TiktokCampaign ||
    item.type === OrderItemType.TiktokPrioritizedLaunch)

export const useTikTokOrderSync = ({
  campaignId,
  budget,
  isPaymentSucceeded,
  currentOrder,
  isOrderFetching,
  onOrderSynced,
}: UseTikTokOrderSyncParams): void => {
  const dispatch = useAppDispatch()
  const initializedOrderIdRef = useRef<number | null>(null)
  const isCreatingOrderRef = useRef(false)

  const [createOrder] = useCreateOrderMutation()
  const [addOrderItem] = useAddOrderItemMutation()
  const [updateOrderItem] = useUpdateOrderItemMutation()
  const [removeOrderItems] = useRemoveOrderItemsMutation()

  const isOrderDataPending = isOrderFetching || currentOrder === void 0

  useEffect(() => {
    if (isPaymentSucceeded || !budget || isOrderDataPending) {
      return
    }

    const syncActiveOrder = async (order: Order): Promise<void> => {
      if (initializedOrderIdRef.current === order.id) {
        return
      }
      initializedOrderIdRef.current = order.id

      const foreignItemIds = order.items
        .filter((item) => !isSameCampaignOrderItem(item, campaignId))
        .map((item) => item.id)

      if (foreignItemIds.length) {
        await removeOrderItems({
          orderId: order.id,
          itemIds: foreignItemIds,
        }).unwrap()
      }

      const existingItem = order.items.find(
        (item) =>
          item.type === OrderItemType.TiktokCampaign &&
          item.sourceId === campaignId,
      )

      if (existingItem) {
        if (existingItem.fullPrice !== budget) {
          await updateOrderItem({
            orderId: order.id,
            itemId: existingItem.id,
            fullPrice: budget,
          }).unwrap()
        }
      } else {
        await addOrderItem({
          orderId: order.id,
          type: OrderItemType.TiktokCampaign,
          sourceId: campaignId,
          fullPrice: budget,
        }).unwrap()
      }

      await onOrderSynced()
    }

    const initializeOrder = async (): Promise<void> => {
      if (!currentOrder) {
        if (isCreatingOrderRef.current) {
          return
        }
        isCreatingOrderRef.current = true
        try {
          await createOrder().unwrap()
        } finally {
          isCreatingOrderRef.current = false
        }
        return
      }

      if (isOrderLocked(currentOrder.status)) {
        initializedOrderIdRef.current = null
        return
      }

      await syncActiveOrder(currentOrder)
    }

    initializeOrder().catch(() => {
      initializedOrderIdRef.current = null
      dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
    })
  }, [
    addOrderItem,
    budget,
    campaignId,
    createOrder,
    currentOrder,
    dispatch,
    isOrderDataPending,
    isPaymentSucceeded,
    onOrderSynced,
    removeOrderItems,
    updateOrderItem,
  ])
}
